import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import RevenueTrendChart from "@/components/churchbuilder/RevenueTrendChart";
import SuperAdminTicketsPanel from "@/components/churchbuilder/SuperAdminTicketsPanel";
import { Loader2, Power, Trash2, Search, ExternalLink, CreditCard, RefreshCw, Crown, AlertTriangle, TrendingUp, Calendar, Users, Globe, FileText, Layers } from "lucide-react";

const STATUS_STYLES = {
  active: "border-emerald-500/40 text-emerald-300",
  suspended: "border-red-500/40 text-red-300",
};

const WSTATUS_PILL = {
  draft:     { label: "Draft",     cls: "border-neutral-500/40 text-neutral-300 bg-neutral-800/40" },
  published: { label: "Published", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-900/20" },
  trial:     { label: "Trial",     cls: "border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/10" },
  paused:    { label: "Paused",    cls: "border-amber-500/40 text-amber-300 bg-amber-900/20" },
  suspended: { label: "Suspended", cls: "border-red-500/40 text-red-300 bg-red-900/20" },
  expired:   { label: "Expired",   cls: "border-red-500/40 text-red-300 bg-red-900/20" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
};

export default function ChurchBuilderAdmin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [churches, setChurches] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [stripeMode, setStripeMode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ch] = await Promise.all([
        api.get("/admin/church-builder/overview"),
        api.get("/admin/church-builder/churches", { params: statusFilter !== "all" ? { status: statusFilter } : {} }),
      ]);
      setOverview(ov.data);
      setChurches(ch.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    load();
    api.get("/admin/stripe-mode").then((r) => setStripeMode(r.data)).catch(() => {});
  }, [user, authLoading, load, navigate]);

  const setStatus = async (c, status) => {
    try {
      await api.patch(`/admin/church-builder/churches/${c.id}/status`, { status });
      toast.success(`${c.name} → ${status}`);
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  const renameChurch = async (c) => {
    const name = window.prompt(`Rename church "${c.name}" to:`, c.name);
    if (!name || name.trim() === c.name) return;
    try {
      await api.patch(`/admin/church-builder/churches/${c.id}/rename`, { name: name.trim() });
      toast.success("Renamed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const changePlan = async (c) => {
    const plan = window.prompt(`Change plan for "${c.name}". Allowed: trial, starter, standard, premium`, c.subscription_plan || "trial");
    if (!plan || !["trial", "starter", "standard", "premium"].includes(plan)) return;
    try {
      await api.patch(`/admin/church-builder/churches/${c.id}/plan`, { subscription_plan: plan });
      toast.success(`Plan → ${plan}`);
      load();
    } catch { toast.error("Plan change failed"); }
  };

  const extendTrial = async (c) => {
    const daysStr = window.prompt(`Extend trial for "${c.name}" by how many days? (0 = expire immediately)`, "7");
    if (daysStr === null) return;
    const days = parseInt(daysStr, 10);
    if (Number.isNaN(days) || days < 0) return toast.error("Enter a non-negative number");
    try {
      await api.patch(`/admin/church-builder/churches/${c.id}/plan`, { subscription_plan: c.subscription_plan || "trial", extend_trial_days: days });
      toast.success(days === 0 ? "Trial expired" : `Trial extended ${days} days`);
      load();
    } catch { toast.error("Failed"); }
  };

  const resetPassword = async (c) => {
    if (!c.owner_user_id) return toast.error("Owner user_id missing");
    const mode = window.confirm(
      `Reset password for ${c.owner_email}?\n\n` +
      `OK = generate temp password (shown once)\nCancel = send reset email link`
    ) ? "temp_password" : "email_link";
    try {
      const { data } = await api.post(`/admin/users/${c.owner_user_id}/reset-password`, { mode });
      if (data.mode === "temp_password") {
        window.alert(`Temp password for ${data.email}:\n\n${data.temp_password}\n\nShare this securely. The user must change it on next login.`);
      } else {
        toast.success(`Reset email sent to ${data.email}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/church-builder/churches/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const filtered = search ? churches.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.slug.toLowerCase().includes(search.toLowerCase()) ||
    (c.owner_email || "").toLowerCase().includes(search.toLowerCase())) : churches;

  if (authLoading || !user) return <div className="max-w-7xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  return (
    <div data-testid="cb-admin" className="max-w-7xl mx-auto px-6 lg:px-10 py-12 text-white">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-3 flex items-center gap-3 flex-wrap">
            Church Builder · Super Admin
            <StripeModeBadge mode={stripeMode} />
          </div>
          <h1 className="font-serif-display text-5xl">All <span className="italic text-[#D4AF37]">tenants</span></h1>
        </div>
      </div>

      {overview && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="cb-admin-analytics">
          <Stat label="Churches Created" value={overview.total} icon={Users} testid="kpi-total" />
          <Stat label="Active Churches" value={overview.active_churches} icon={Globe} testid="kpi-active" />
          <Stat label="Trial Churches" value={overview.trial_churches ?? overview.trial} icon={Calendar} testid="kpi-trial" />
          <Stat label="Premium Churches" value={overview.premium_churches} icon={Crown} testid="kpi-premium" />
          <Stat label="Published Sites" value={overview.published_sites ?? overview.active_published} icon={Globe} testid="kpi-published" />
          <Stat label="Monthly Revenue" value={fmtMoney(overview.monthly_revenue_pence, overview.currency)} icon={TrendingUp} testid="kpi-mrr" />
          <Stat label="Annual Revenue" value={fmtMoney(overview.annual_revenue_pence, overview.currency)} icon={TrendingUp} testid="kpi-arr" />
          <Stat label="Expiring Trials (3d)" value={overview.expiring_trials} icon={Calendar} testid="kpi-expiring" tone={overview.expiring_trials > 0 ? "amber" : null} />
          <Stat label="Failed Payments" value={overview.failed_payments} icon={AlertTriangle} testid="kpi-failed" tone={overview.failed_payments > 0 ? "red" : null} />
          <Stat label="Most-used Template" value={overview.most_used_template || "—"} icon={Layers} testid="kpi-template" />
        </div>
      )}

      <StripeCatalogPanel />
      <SuperAdminTicketsPanel />
      <RevenueTrendChart />

      <div className="mt-10 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input data-testid="cb-admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name / slug / owner email" className="w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none pl-9 pr-3 py-2 rounded text-sm text-white" />
        </div>
        <div className="flex gap-2">
          {["all", "active", "suspended"].map((s) => (
            <button key={s} data-testid={`cb-admin-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded border ${statusFilter === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-300"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
            <th className="text-left py-3">Church</th>
            <th>Owner</th>
            <th>Plan</th>
            <th>Sub status</th>
            <th>Trial ends</th>
            <th>Next billing</th>
            <th>Last payment</th>
            <th>Stripe ID</th>
            <th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="py-8 text-center text-neutral-500"><Loader2 className="inline animate-spin" size={16} /></td></tr> :
              filtered.length === 0 ? <tr><td colSpan={9} className="py-8 text-center text-neutral-500">No churches.</td></tr> :
              filtered.map((c) => {
                const failed = c.last_payment_status === "failed";
                return (
                <tr key={c.id} data-testid={`cb-admin-row-${c.id}`} className="border-b border-white/5">
                  <td className="py-3">
                    <div className="text-white">{c.name}</div>
                    <div className="text-xs text-neutral-500">/{c.slug} · {c.template_id}</div>
                    <div className="text-[10px] mt-1 inline-flex gap-2 flex-wrap">
                      <span className={`tracking-widest uppercase border px-2 py-0.5 rounded ${STATUS_STYLES[c.status] || ""}`}>{c.status || "active"}</span>
                      <span data-testid={`cb-admin-wstatus-${c.id}`} className={`tracking-widest uppercase border px-2 py-0.5 rounded ${WSTATUS_PILL[c.website_status]?.cls || "border-neutral-500/40 text-neutral-300"}`}>{WSTATUS_PILL[c.website_status]?.label || c.website_status || "draft"}</span>
                      <span className={`tracking-widest uppercase ${c.is_published ? "text-emerald-400" : "text-neutral-500"}`}>{c.is_published ? "● live" : "○ draft"}</span>
                    </div>
                  </td>
                  <td className="text-neutral-300 text-xs">{c.owner_email || "—"}</td>
                  <td className="text-[#D4AF37] text-xs" data-testid={`cb-admin-plan-${c.id}`}>{c.subscription_plan}{c.subscription_interval ? ` · ${c.subscription_interval}` : ""}</td>
                  <td className="text-xs" data-testid={`cb-admin-substatus-${c.id}`}>{c.subscription_status ? <span className="text-emerald-300">{c.subscription_status}</span> : <span className="text-neutral-500">—</span>}</td>
                  <td className="text-xs text-neutral-300">{fmtDate(c.trial_end_at || c.trial_ends_at)}</td>
                  <td className="text-xs text-neutral-300">{fmtDate(c.next_billing_at || c.current_period_end)}</td>
                  <td className={`text-xs ${failed ? "text-red-300" : "text-neutral-300"}`} data-testid={`cb-admin-lastpayment-${c.id}`}>{c.last_payment_status || "—"}</td>
                  <td className="text-[10px] text-neutral-500" data-testid={`cb-admin-stripe-${c.id}`}>{(c.stripe_customer_id || "").slice(0, 14) || "—"}</td>
                  <td className="text-right whitespace-nowrap">
                    <a href={`/church-builder/${c.slug}/dashboard?impersonate=1`} title="Impersonate this church" data-testid={`cb-admin-impersonate-${c.id}`} className="text-[#D4AF37] hover:opacity-80 p-2 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-semibold">Impersonate</a>
                    <a href={`/church-builder/${c.slug}/dashboard`} target="_blank" rel="noreferrer" title="Open dashboard" data-testid={`cb-admin-open-${c.id}`} className="text-neutral-400 hover:text-[#D4AF37] p-2 inline-block">Open</a>
                    <a href={`/churches/${c.slug}`} target="_blank" rel="noreferrer" title="View" className="text-neutral-400 hover:text-[#D4AF37] p-2 inline-block"><ExternalLink size={13} /></a>
                    <button data-testid={`cb-admin-rename-${c.id}`} title="Rename" onClick={() => renameChurch(c)} className="text-neutral-400 hover:text-[#D4AF37] p-2 text-[10px] tracking-widest uppercase">Rename</button>
                    <button data-testid={`cb-admin-plan-${c.id}-edit`} title="Change plan" onClick={() => changePlan(c)} className="text-neutral-400 hover:text-[#D4AF37] p-2 text-[10px] tracking-widest uppercase">Plan</button>
                    <button data-testid={`cb-admin-trial-${c.id}`} title="Extend trial" onClick={() => extendTrial(c)} className="text-neutral-400 hover:text-[#D4AF37] p-2 text-[10px] tracking-widest uppercase">Trial</button>
                    <button data-testid={`cb-admin-pwreset-${c.id}`} title="Reset password" onClick={() => resetPassword(c)} className="text-neutral-400 hover:text-[#D4AF37] p-2 text-[10px] tracking-widest uppercase">Pw</button>
                    <button data-testid={`cb-admin-toggle-${c.id}`} title={c.status === "suspended" ? "Activate" : "Suspend"} onClick={() => setStatus(c, c.status === "suspended" ? "active" : "suspended")}
                      className={`p-2 ${c.status === "suspended" ? "text-emerald-400" : "text-amber-400"} hover:opacity-80`}><Power size={13} /></button>
                    <button data-testid={`cb-admin-delete-${c.id}`} title="Delete" onClick={() => remove(c)} className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={13} /></button>
                  </td>
                </tr>
              );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const fmtMoney = (pence, currency) => {
  const sym = (currency || "gbp").toLowerCase() === "gbp" ? "£" : (currency || "").toUpperCase();
  const pounds = (pence || 0) / 100;
  // Show pence-level precision when the amount isn't a whole pound (e.g. £49.50)
  // but trim trailing .00 for whole pounds so the KPI cards stay tidy.
  const isWhole = Math.round(pounds) === pounds;
  return `${sym}${pounds.toLocaleString(undefined, {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

function StripeModeBadge({ mode }) {
  if (!mode) return null;
  const m = mode.mode || "unconfigured";
  const conf = mode.configured;
  if (m === "live") {
    return <span data-testid="stripe-mode-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-red-500/60 bg-red-900/30 text-red-200"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE — REAL MONEY</span>;
  }
  if (m === "test") {
    return <span data-testid="stripe-mode-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-emerald-500/40 bg-emerald-900/20 text-emerald-300"><span className="w-2 h-2 rounded-full bg-emerald-500" /> TEST MODE</span>;
  }
  return <span data-testid="stripe-mode-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase border border-amber-500/40 bg-amber-900/20 text-amber-300">Stripe not configured</span>;
}

function Stat({ label, value, icon: Icon, testid, tone }) {
  const toneClass = tone === "red" ? "border-red-500/40 bg-red-900/10" : tone === "amber" ? "border-amber-500/40 bg-amber-900/10" : "border-white/10";
  return (
    <div data-testid={testid} className={`luxury-card rounded-xl p-5 border ${toneClass}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
        {Icon && <Icon size={13} className="text-[#D4AF37] opacity-70" />}
      </div>
      <div className="font-serif-display text-3xl text-white mt-2 truncate" title={typeof value === "string" ? value : ""}>{value}</div>
    </div>
  );
}


function StripeCatalogPanel() {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState([]);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, dg] = await Promise.all([
        api.get("/admin/church-builder/stripe-catalog"),
        api.get("/admin/church-builder/stripe-diagnostic"),
      ]);
      setItems(cat.data.items || []);
      setDiag(dg.data || null);
    } catch (e) { toast.error("Could not load Stripe catalog"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (expanded) load(); }, [expanded]);

  const reprovision = async () => {
    if (!window.confirm(
      "Flush the cached Stripe catalog and create fresh products + prices?\n\n" +
      "• This does NOT delete existing products in your Stripe dashboard.\n" +
      "• Live subscriptions keep working — they're already tied to the old price IDs.\n" +
      "• Only NEW signups after this will use the freshly-created prices.\n\n" +
      "Use this after rotating Stripe accounts or renaming products."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/church-builder/stripe-catalog/reprovision");
      toast.success(`Cleared ${data.cleared}, provisioned ${data.provisioned} fresh Stripe prices (mode: ${data.mode || "?"})`);
      setItems(data.items || []);
      // Refresh diagnostic snapshot after reprovision
      try {
        const dg = await api.get("/admin/church-builder/stripe-diagnostic");
        setDiag(dg.data || null);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-provision failed (check Stripe key)");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="cb-admin-catalog-panel" className="luxury-card rounded-xl p-5 mt-6 border border-[#D4AF37]/15">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CreditCard size={16} className="text-[#D4AF37]" />
          <div>
            <div className="font-serif-display text-xl">Stripe Catalog</div>
            <div className="text-xs text-neutral-400 mt-0.5">Cached product + price IDs that drive subscription checkout.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="cb-admin-catalog-toggle"
            onClick={() => setExpanded((p) => !p)}
            className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase"
          >
            {expanded ? "Hide" : "Show"} catalog
          </button>
          <button
            type="button"
            data-testid="cb-admin-catalog-reprovision"
            onClick={reprovision}
            disabled={busy}
            className="btn-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Flush & re-provision
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 space-y-5" data-testid="cb-admin-catalog-list">
          {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> : (
            <>
              {diag && <DiagnosticBlock diag={diag} />}
              <div className="overflow-x-auto">
                {items.length === 0 ? <div className="text-xs text-neutral-500">No catalog entries yet — click &quot;Flush &amp; re-provision&quot; (this writes to <span className="font-mono">stripe_catalog</span> in this environment&apos;s MongoDB).</div> :
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-white/10 text-[10px] tracking-widest uppercase text-neutral-500">
                    <th className="text-left py-2">Plan</th>
                    <th>Interval</th>
                    <th>Product ID</th>
                    <th>Price ID</th>
                    <th>Amount</th>
                  </tr></thead>
                  <tbody>
                    {items.filter((it) => it.key !== "__meta__").map((it) => (
                      <tr key={it.key} className="border-b border-white/5">
                        <td className="py-2 text-white capitalize">{it.plan_id}</td>
                        <td className="text-neutral-400">{it.interval}</td>
                        <td className="text-neutral-500 font-mono text-[10px]">{it.product_id}</td>
                        <td className="text-neutral-500 font-mono text-[10px]">{it.price_id}</td>
                        <td className="text-[#D4AF37]">{((it.unit_amount || 0) / 100).toFixed(2)} {(it.currency || "gbp").toUpperCase()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DiagnosticBlock({ diag }) {
  const env = diag?.env || {};
  const eff = diag?.effective_subscriptions_key || {};
  const cat = diag?.catalog || {};
  const ping = diag?.stripe_auth_ping || {};
  const verdict = diag?.verdict;
  const ok = verdict === "OK";
  return (
    <div data-testid="cb-admin-stripe-diag" className={`rounded-lg border p-4 ${ok ? "border-emerald-500/30 bg-emerald-900/10" : "border-amber-500/40 bg-amber-900/10"}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] tracking-widest uppercase text-neutral-400">Live diagnostic</div>
        <span className={`text-[10px] tracking-widest uppercase font-bold ${ok ? "text-emerald-300" : "text-amber-300"}`}>Verdict: {verdict}</span>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
        <DRow k="STRIPE_API_KEY" v={`${env.STRIPE_API_KEY?.mode || "—"}  ${env.STRIPE_API_KEY?.preview || ""}`} />
        <DRow k="STRIPE_SUBSCRIPTIONS_KEY" v={`${env.STRIPE_SUBSCRIPTIONS_KEY?.mode || "—"}  ${env.STRIPE_SUBSCRIPTIONS_KEY?.preview || ""}`} />
        <DRow k="STRIPE_WEBHOOK_SECRET" v={env.STRIPE_WEBHOOK_SECRET?.set ? env.STRIPE_WEBHOOK_SECRET.preview : "unset"} />
        <DRow k="SITE_URL" v={env.SITE_URL?.value || "unset"} />
        <DRow k="Effective key (for subs)" v={`${eff.mode}  ${eff.preview || ""}`} />
        <DRow k="Catalog meta mode" v={cat.meta?.mode || "—"} />
        <DRow k="Catalog rows" v={`${cat.row_count} / ${cat.expected_count}`} />
        <DRow k="Stripe auth ping" v={ping.checked ? (ping.ok ? `✓ ${ping.account_id || "ok"}` : "✗ rejected") : "skipped"} tone={ping.checked ? (ping.ok ? "ok" : "bad") : null} />
      </div>
      {ping.checked && !ping.ok && (
        <div className="mt-4 rounded border border-red-500/40 bg-red-950/30 p-3 text-[11px] text-red-200">
          <div className="font-semibold tracking-widest uppercase text-[10px] text-red-300">Stripe rejected the live key</div>
          <div className="font-mono mt-2 break-words">{ping.error}</div>
          {ping.hint && <div className="mt-3 text-amber-200">{ping.hint}</div>}
        </div>
      )}
    </div>
  );
}

function DRow({ k, v, tone }) {
  const toneCls = tone === "ok" ? "text-emerald-300" : tone === "bad" ? "text-red-300" : "text-white";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-white/5 py-1">
      <span className="text-[10px] tracking-widest uppercase text-neutral-500">{k}</span>
      <span className={`font-mono text-[11px] ${toneCls} truncate max-w-[60%] text-right`} title={String(v ?? "")}>{String(v ?? "—")}</span>
    </div>
  );
}
